import {
  ISnowtamRwy,
  IFriction,
  IDeposit,
  IContaminantGroup,
} from "@naviair-utm/node-shared-interfaces";
import * as regexes from "./SnowtamRegExp";
import {
  contaminationCodes,
  frictionInstruments,
} from "./SnowtamAbbreviations";
import {
  getIntFromRegExp,
  getMatch,
  getStringFromRegExp,
} from "../CommonParsers";
import { generateUTCDate } from "../Date";

/**
 * Get the ICAO code for the snowtam
 * @param input The snowtam message
 * @returns ICAO code
 */
export const icaoParser = (input: string): string => {
  const icao: string = getMatch(input.match(regexes.icaoRegex));
  return icao !== "" ? icao : "";
};

/**
 * Get the date/time from snowtam
 * @param dateTime The snowtam message
 * @returns ISnowtamDateTime object
 */
export const dateTimeParser = (dateTime: string): Date => {
  // Get the 8 digits from date and time
  if (dateTime && dateTime.length === 8) {
    const month = parseInt(dateTime.substr(0, 2));
    const day = parseInt(dateTime.substr(2, 2));
    const hour = parseInt(dateTime.substr(4, 2));
    const minute = parseInt(dateTime.substr(6, 2));
    return generateUTCDate(minute, hour, day, month).toDate();
  } else {
    return undefined;
  }
};

/**
 * Parse runways from the snowtam and return an array of runways
 * @param input The snowtam message
 * @returns Array of ISnowtamRwy
 */
export const rwyParser = (input: string): ISnowtamRwy[] => {
  let returnObj: Array<ISnowtamRwy> = [];

  const rwys: RegExpMatchArray | null = input.match(regexes.rwyRegex);
  if (rwys !== null) {
    // Go through each runway if more than 1 is provided
    for (const rwy of rwys) {
      let rwyObj: ISnowtamRwy = {};

      // Get the identifier, eg. "05L";
      const rwyIdent: string | null = getStringFromRegExp(
        rwy,
        regexes.rwyIdentRegex
      );
      if (rwyIdent !== null) {
        rwyObj.identifier = rwyIdent;
      }

      // Get the cleared length if present, eg. "3000"
      const clearedLength: number | null = getIntFromRegExp(
        rwy,
        regexes.rwyClearedLengthRegex
      );
      if (clearedLength !== null) {
        rwyObj.clearedLength = clearedLength;
      }

      // Get the cleared width if present, eg. "50"
      const clearedWidth: number | null = getIntFromRegExp(
        rwy,
        regexes.rwyClearedWidthRegex
      );
      if (clearedWidth !== null) {
        rwyObj.clearedWidth = clearedWidth;
      }

      // Get the object of deposits
      const deposits: IDeposit | null = depositsParser(rwy);
      if (deposits !== null) {
        rwyObj.deposits = deposits;
      }

      // Get the friction for
      const friction: IFriction = rwyFrictionParser(rwy);
      if (friction !== null) {
        rwyObj.friction = friction;
      }

      // Get if any snowbanks
      const snowbanks: string | null = getStringFromRegExp(
        rwy,
        regexes.snowbanksRegex
      );
      if (snowbanks !== null) {
        rwyObj.snowbanks = snowbanks;
      }

      // Get if any notes on runway lights
      const rwyLights: string | null = getStringFromRegExp(
        rwy,
        regexes.rwyLightsRegex
      );
      if (rwyLights !== null) {
        rwyObj.rwyLights = rwyLights;
      }

      // Get if any note on clearing starting
      const clearingStart: string | null = getStringFromRegExp(
        rwy,
        regexes.clearingStartRegex
      );
      if (clearingStart !== null) {
        rwyObj.clearingStart = clearingStart;
      }

      // Get if any note on clearing end
      const clearingEnd: string | null = getStringFromRegExp(
        rwy,
        regexes.clearingEndRegex
      );
      if (clearingEnd !== null) {
        rwyObj.clearingEnd = clearingEnd;
      }

      // Get if any notes on the taxi ways related to this runway
      const taxiWays: string | null = getStringFromRegExp(
        rwy,
        regexes.taxiWaysRegex
      );
      if (taxiWays !== null) {
        rwyObj.taxiWays = taxiWays;
      }

      returnObj.push(rwyObj);
    }
  }

  return returnObj;
};

/**
 * Parse runways from the snowtam in grf format of provided in such format and return an array of runways
 * @param input The snowtam message
 * @returns Array of ISnowtamRwy
 */
export const grfRwyParser = (input: string): ISnowtamRwy[] => {
  let returnObj: Array<ISnowtamRwy> = [];

  const rwys: RegExpMatchArray | null = input.match(regexes.grfRwyRegex);
  if (rwys !== null) {
    // Go through each runway if more than 1 is provided
    for (const rwy of rwys) {
      let rwyObj: ISnowtamRwy = {};

      // Get the identifier, eg. "05L";
      const rwyIdent: string | null = getStringFromRegExp(
        rwy,
        regexes.grfRwyIdentRegex
      );
      if (rwyIdent !== null) {
        rwyObj.identifier = rwyIdent;
      }

      // Get date and time for observation
      const dayTime: Date | null = dateTimeParser(
        getStringFromRegExp(rwy, regexes.grfDayTimeRegex)
      );
      if (dayTime) {
        rwyObj.dayTime = dayTime;
      }

      // Create 3 contamination groups (One for each third of rwy)
      let contaminationGroups: Array<IContaminantGroup> = [{}, {}, {}];

      const rwyConditionStr: string | null = getStringFromRegExp(
        rwy,
        regexes.grfRwyContaminants
      );
      if (rwyConditionStr !== null) {
        // loop through each contaminant value and add to corresponding contaminantGroup (eg. '5/5/5', '100/100/100', 'NR/NR/03', 'WET/WET/SLUSH')
        rwyConditionStr
          .match(regexes.grfRwyContaminantsGroups)
          ?.forEach((item, index) => {
            getThreeNumbersSlash(item)?.forEach((value, i) => {
              // rwy condition codes
              if (index === 0 && typeof value === "number") {
                contaminationGroups[i].rwyConditionCode = value;
              }

              // percent coverage contaminent
              if (index === 1) {
                contaminationGroups[i].percentCoverages = value;
              }

              // depth of loose contaminent
              if (index === 2) {
                contaminationGroups[i].depth = value;
              }

              // Acondition description of loose contaminent
              if (index === 3 && typeof value === "string") {
                contaminationGroups[i].description = value;
              }
            });

            // width of runway to which the contaminent codes applies (if less than published width)
            if (index === 4) {
              const clearedWidth =
                typeof item === "number" ? item : parseInt(item);
              if (!isNaN(clearedWidth)) {
                rwyObj.clearedWidth = clearedWidth;
              }
            }
          });
      }
      rwyObj.contaminantGroups = contaminationGroups;

      returnObj.push(rwyObj);
    }
  }

  return returnObj;
};

/**
 * For use internally. Get deposits on runways
 * @param input Runway string (parsed with runwayParser)
 * @returns IDeposit or null if deposits not specified
 */
const depositsParser = (input: string): IDeposit | null => {
  let returnObj: IDeposit = {};

  // Get deposit codes and description
  const deposits: RegExpMatchArray | null = input.match(regexes.depositsRegex);
  if (deposits !== null) {
    const depositsString = getMatch(deposits);
    if (depositsString !== "") {
      returnObj.code = getThreeNumbersSlash(depositsString);

      // Add a description to the code
      returnObj.description = [];
      for (let c of returnObj.code) {
        if (typeof c == "number" && c in contaminationCodes) {
          returnObj.description.push(contaminationCodes[c]);
        } else if (c === "NIL") {
          returnObj.description.push("Clear and dry");
        } else {
          returnObj.description.push("Unknown contaminant");
        }
      }
    }
  }

  // Get the depth of deposits/contaminations
  const depositsDepths: RegExpMatchArray | null = input.match(
    regexes.depthOfDepositsRegex
  );
  if (depositsDepths !== null) {
    const depositsDepthsString = getMatch(depositsDepths);
    if (depositsDepthsString !== "") {
      returnObj.depth = getThreeNumbersSlash(depositsDepthsString);
    }
  }

  return returnObj;
};

/**
 * For use internally. Get friction on runways
 * @param input Runway string (parsed with runwayParser)
 * @returns IFriction or null if friction not specified
 */
const rwyFrictionParser = (input: string): IFriction | null => {
  const frictions = input.match(regexes.frictionRegex);

  if (frictions !== null) {
    const frictionsString = getMatch(frictions);
    if (frictionsString !== "") {
      const frictionsArray = getThreeNumbersSlash(frictionsString);
      let frictionObj: IFriction = {
        friction: frictionsArray,
      };

      // Use the first element to determine whether the frictions are estimated or measured
      if (
        typeof frictionsArray[0] === "number" &&
        frictionsArray[0].toString().length === 1
      ) {
        frictionObj.frictionMeasured = false;
      } else if (
        typeof frictionsArray[0] === "number" &&
        frictionsArray[0].toString().length === 2
      ) {
        frictionObj.frictionMeasured = true;
      }

      // Check if the measuring instrument has been provided
      if (frictions[2] !== undefined) {
        frictionObj.instrumentAbbreviation = frictions[2];
        if (frictions[2] in frictionInstruments) {
          frictionObj.instrumentDescription = frictionInstruments[frictions[2]];
        }
      }

      return frictionObj;
    } else {
      return null;
    }
  } else {
    return null;
  }
};

/**
 * Get the next planned observation of runway condition
 * @param input The snowtam message
 * @returns ISnowtamDateTime or null if not specified
 */
export const nextPlannedObservationParser = (
  input: string
): Date | undefined => {
  const nextPlannedObservation: string = getMatch(
    input.match(regexes.nextPlannedObservationRegex)
  ).trim();

  if (nextPlannedObservation !== "") {
    // Get the 8 digits at the form of dateTime
    if (nextPlannedObservation.length === 8) {
      const month = parseInt(nextPlannedObservation.substr(0, 2));
      const day = parseInt(nextPlannedObservation.substr(2, 2));
      const hour = parseInt(nextPlannedObservation.substr(4, 2));
      const minute = parseInt(nextPlannedObservation.substr(6, 2));
      return generateUTCDate(minute, hour, day, month).toDate();
    } else {
      return undefined;
    }
  } else {
    return undefined;
  }
};

/*
    A function to get an array wither strings or numbers from the snowtam. 
    There are multiple strings which contains data for each third of the runway - therefore an array of 3 elements. 
    The elements are parsed as numbers if input looks like a number. 
*/
const getThreeNumbersSlash = (input: string): Array<number | string> | null => {
  // Trim and split by slash
  input = input.trim();
  let inputSplitted = input.split("/");

  if (inputSplitted.length === 3) {
    // Parse as integers if a number or push string if not
    let returnArray: Array<number | string> = [];
    for (let d of inputSplitted) {
      if (!isNaN(+d)) {
        returnArray.push(parseInt(d));
      } else {
        // If they are not numbers, this is the fallback without parsing as integers
        returnArray.push(d);
      }
    }

    return returnArray;
  } else {
    return null;
  }
};
